import { useState } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Paper,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
// ⚡ Path-based imports (6× faster than barrel imports)
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { analytics } from '@/lib/analytics';

interface SpotifyTrack {
  id: string;
  uri: string;
  name: string;
  artists: Array<{ name: string }>;
  album: {
    name: string;
    images: Array<{ url: string; height: number; width: number }>;
  };
  duration_ms: number;
}

interface SpotifySearchProps {
  accessToken?: string | null;
  onTrackSelect: (track: SpotifyTrack) => void;
}

export function SpotifySearch({ accessToken, onTrackSelect }: SpotifySearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SpotifyTrack[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ q: searchQuery });
      if (accessToken) {
        params.append('token', accessToken);
      }

      const response = await fetch(`/api/spotify/search?${params}`);
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        setResults([]);
        analytics.searchExecuted(searchQuery, 'spotify', 0);
      } else {
        const items = data.tracks?.items || [];
        setResults(items);
        analytics.searchExecuted(searchQuery, 'spotify', items.length);
      }
    } catch (err) {
      console.error('Failed to search tracks:', err);
      setError('Failed to search tracks');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(query);
  };

  const formatDuration = (ms: number) => {
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          placeholder="Search for a track..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: query && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setQuery('');
                    setResults([]);
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </form>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ py: 2 }}>
          {error}
        </Typography>
      )}

      {results.length > 0 && (
        <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
          <List>
            {results.map((track) => (
              <ListItem key={track.id} disablePadding>
                <ListItemButton onClick={() => {
                  analytics.spotifyTrackSelected(track.name, 'search');
                  onTrackSelect(track);
                }}>
                  <ListItemAvatar>
                    <Avatar
                      src={track.album.images[2]?.url || track.album.images[0]?.url}
                      alt={track.album.name}
                      variant="rounded"
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={track.name}
                    secondary={
                      <span>
                        {track.artists.map((a) => a.name).join(', ')} • {track.album.name} • {formatDuration(track.duration_ms)}
                      </span>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
